// src/stores/languageStore.ts
import { createSignal, createMemo } from "solid-js";
import * as i18n from "@solid-primitives/i18n";
import en from "../translations/en";
import it from "../translations/it";
import de from "../translations/de";
import es from "../translations/es";
import gr from "../translations/gr";
import fr from "../translations/fr";
import sa from "../translations/sa";
import tr from "../translations/tr";
import cn from "../translations/cn";

const dictionaries: Record<string, Record<string, string>> = {
  en,
  it,
  es,
  de,
  gr,
  fr,
  sa,
  cn,
  tr,
};

/** BCP-47 locale codes for Intl APIs (e.g. date / number formatting). */
const BCP47: Record<string, string> = {
  en: "en-US",
  it: "it-IT",
  es: "es-ES",
  de: "de-DE",
  gr: "el-GR",
  fr: "fr-FR",
  sa: "ar-SA",
  cn: "zh-CN",
  tr: "tr-TR",
};

const [locale, setLocale] = createSignal("en");
const [isRTL, setIsRTL] = createSignal(false);

// ── Initialise from localStorage on module load ──────────────────────────────
const savedLocale = localStorage.getItem("app_locale") || "en";
const savedDirection = localStorage.getItem("textDirection") || "ltr";
setLocale(savedLocale);
setIsRTL(savedDirection === "rtl");

// Apply to DOM immediately so CSS / browser APIs read the correct direction.
if (typeof document !== "undefined") {
  document.documentElement.dir = savedDirection;
  document.documentElement.lang = BCP47[savedLocale] ?? savedLocale;
}

// ── Language switcher ─────────────────────────────────────────────────────────
const changeLanguage = (lang: string) => {
  setLocale(lang);

  const rtl = lang === "sa";
  const direction = rtl ? "rtl" : "ltr";

  setIsRTL(rtl);

  localStorage.setItem("app_locale", lang);
  localStorage.setItem("language", lang);
  localStorage.setItem("textDirection", direction);

  if (typeof document !== "undefined") {
    // These two lines are the critical fix: Lightning.js canvas + CSS both
    // need the DOM attribute to know the layout direction.
    document.documentElement.dir = direction;
    document.documentElement.lang = BCP47[lang] ?? lang;
  }
};

const dict = createMemo(() => i18n.flatten(dictionaries[locale()] ?? dictionaries["en"]));
const t = i18n.translator(dict);

/** X offset for the left-side navigation menu. */
const menuX = () => (isRTL() ? 1350 : 60);

/** Active font family — NotoSansArabic for Arabic, Roboto otherwise. */
const fontFamily = () => (isRTL() ? "NotoSansArabic" : "Roboto");

/** Current BCP-47 locale code for Intl APIs (e.g. toLocaleDateString). */
const intlLocale = () => BCP47[locale()] ?? "en-US";

export { locale, setLocale, changeLanguage, t, isRTL, menuX, fontFamily, intlLocale };